/**
 * Pointer/gesture surface for the editor canvas.
 *
 * Normalises Pointer Events (touch, Apple Pencil, mouse, trackpad) into:
 *   • single-pointer tool strokes (down / move with coalesced samples / up)
 *   • tap, double-tap and long-press
 *   • two-finger pan + pinch (view updates)
 *   • wheel pan and ⌘/ctrl+wheel zoom (desktop / trackpad scroll)
 *   • pen hover
 *
 * WebKit specifics handled here: `touch-action: none` on the surface, non-passive
 * touchmove/gesture* preventDefault (page zoom, Scribble), palm rejection while a
 * pen is active, `getCoalescedEvents()` for 120–240 Hz Pencil sampling.
 */
import type { Vec } from '../../shared/doc.ts';

export type PointerKind = 'touch' | 'pen' | 'mouse';

export interface Sample extends Vec {
  pressure: number;
  t: number;
}

export interface StrokeInfo {
  kind: PointerKind;
  shift: boolean;
  alt: boolean;
  meta: boolean;
  /** Secondary button / two-finger click. */
  secondary: boolean;
}

/**
 * What a single pointer should do when it lands:
 *  grab – drag something that is already there (starts the tool stroke now)
 *  draw – start marking immediately (Apple Pencil)
 *  hold – pan if it moves right away; mark if it rests for a moment first
 *  point – place or adjust a point: the tool stroke starts now, for any pointer, and a long press is only a longer press
 *  pan  – pan the view
 */
export type DownAction = 'grab' | 'draw' | 'hold' | 'point' | 'pan';

export interface SurfaceHandlers {
  /** Decide what a single pointer does. */
  onDown(p: Sample, info: StrokeInfo): DownAction;
  /** A held pointer became a marking stroke (called before the first onMove). */
  onDrawStart?(p: Sample, info: StrokeInfo): void;
  /** Hold feedback: the pointer is resting and will arm after holdTime. */
  onHoldStart?(p: Sample): void;
  onHoldCancel?(): void;
  onMove(samples: Sample[], last: Sample, info: StrokeInfo): void;
  /** `held`: how long the pointer was down (ms). */
  onUp(p: Sample, info: StrokeInfo, wasTap: boolean, held: number): void;
  onCancel(): void;
  /** A single pointer that panned (or did nothing) was released, or its pan was cut short: the view it left is the view. */
  onPanEnd?(wasTap: boolean): void;
  /** Fires for every tap (tool strokes and empty-space taps alike), before double-tap resolution. */
  onTap?(p: Sample, info: StrokeInfo): void;
  onDoubleTap?(p: Sample, info: StrokeInfo): void;
  onLongPress?(p: Sample, info: StrokeInfo): void;
  onHover?(p: Sample | null): void;
  /** Two-finger gesture: previous & current positions of both pointers. */
  onPinch(a0: Vec, b0: Vec, a1: Vec, b1: Vec): void;
  onPinchEnd?(): void;
  /** Two fingers touched and lifted together without moving (undo, by the iPad drawing apps' convention). */
  onTwoFingerTap?(): void;
  onWheelPan(dx: number, dy: number): void;
  onWheelZoom(factor: number, pivot: Vec): void;
  /** Safari trackpad pinch (GestureEvent): incremental zoom around a pivot, then a commit. */
  onGestureStart?(): void;
  onGestureZoom?(factor: number, pivot: Vec): void;
  onGestureEnd?(): void;
}

/** Safari's non-standard GestureEvent (macOS trackpad pinch, also iOS touch pinch). */
interface SafariGestureEvent extends Event {
  scale?: number;
  clientX?: number;
  clientY?: number;
}

export interface SurfaceOptions {
  /** When a pen has been used, single-finger touches pan instead of drawing. */
  fingerPansAfterPen: boolean;
  tapSlop: number; // CSS px
  tapTime: number; // ms
  longPressTime: number; // ms
  doubleTapTime: number; // ms
  holdTime: number; // ms a finger must rest before a drag becomes a mark
  twoFingerTapTime: number; // ms within which two fingers touch and both lift again for a two-finger tap
}

/** How near the stage's edge a thumb holding the iPad rests, px. */
const EDGE = 28;
const DEFAULTS: SurfaceOptions = { fingerPansAfterPen: true, tapSlop: 8, tapTime: 350, longPressTime: 600, doubleTapTime: 320, holdTime: 180, twoFingerTapTime: 300 };

interface Active {
  id: number;
  kind: PointerKind;
  start: Sample;
  last: Sample;
  moved: boolean;
  info: StrokeInfo;
  role: 'tool' | 'pan' | 'pinch' | 'ignored' | 'hold';
  longTimer: number;
  longFired: boolean;
  holdTimer: number;
}

export class Surface {
  readonly el: HTMLElement;
  readonly opts: SurfaceOptions;
  #h: SurfaceHandlers;
  #active = new Map<number, Active>();
  #rect: DOMRect;
  #penSeen = false;
  #lastTap: { t: number; p: Vec } | null = null;
  #pinchPrev: [Vec, Vec] | null = null;
  /** A two-finger touch that may yet be a tap: when it began, and whether either finger has moved. */
  #pair: { t0: number; moved: boolean; lifted: number } | null = null;
  #enabled = true;
  #onPan: ((dx: number, dy: number) => void) | null = null;
  #ac = new AbortController();

  constructor(el: HTMLElement, handlers: SurfaceHandlers, opts: Partial<SurfaceOptions> = {}) {
    this.el = el;
    this.#h = handlers;
    this.opts = { ...DEFAULTS, ...opts };
    this.#rect = el.getBoundingClientRect();
    const s = this.#ac.signal;
    el.style.touchAction = 'none';
    el.style.userSelect = 'none';
    el.style.setProperty('-webkit-user-select', 'none');
    el.style.setProperty('-webkit-touch-callout', 'none');
    el.addEventListener('pointerdown', this.#down, { signal: s });
    el.addEventListener('pointermove', this.#move, { signal: s });
    el.addEventListener('pointerup', this.#up, { signal: s });
    el.addEventListener('pointercancel', this.#cancel, { signal: s });
    el.addEventListener('pointerleave', this.#leave, { signal: s });
    el.addEventListener('lostpointercapture', this.#lost, { signal: s });
    el.addEventListener('contextmenu', (e) => e.preventDefault(), { signal: s });
    el.addEventListener('wheel', this.#wheel, { passive: false, signal: s });
    // WebKit: block native pinch-zoom / Scribble / double-tap zoom on the surface.
    const block = (e: Event) => {
      if (e.cancelable) e.preventDefault();
    };
    el.addEventListener('touchstart', block, { passive: false, signal: s });
    el.addEventListener('touchmove', block, { passive: false, signal: s });
    el.addEventListener('touchend', block, { passive: false, signal: s });
    // Safari GestureEvents: on a Mac trackpad they are the only signal for a pinch (no wheel
    // events are sent), so they drive zoom. On iPad a touch pinch also fires them, but the
    // pointer-based pinch already handles that case, so they are ignored while fingers are down.
    el.addEventListener('gesturestart', this.#gestureStart, { passive: false, signal: s });
    el.addEventListener('gesturechange', this.#gestureChange, { passive: false, signal: s });
    el.addEventListener('gestureend', this.#gestureEnd, { passive: false, signal: s });
    el.addEventListener('dblclick', block, { signal: s });
  }

  /** Single-finger pan callback used when `fingerPansAfterPen` engages (or the pan tool). */
  set panHandler(fn: ((dx: number, dy: number) => void) | null) {
    this.#onPan = fn;
  }
  /** Force single pointers to pan (e.g. Select tool on empty space is decided by the editor, this is for a Hand tool). */
  panMode = false;

  get penSeen(): boolean {
    return this.#penSeen;
  }
  set enabled(v: boolean) {
    this.#enabled = v;
    if (!v) this.#cancelAll();
  }
  destroy(): void {
    this.#ac.abort();
    this.#cancelAll();
  }

  #sample(e: PointerEvent, r = this.#rect): Sample {
    return { x: e.clientX - r.left, y: e.clientY - r.top, pressure: e.pressure || 0.5, t: e.timeStamp };
  }
  #kind(e: PointerEvent): PointerKind {
    return e.pointerType === 'pen' ? 'pen' : e.pointerType === 'touch' ? 'touch' : 'mouse';
  }
  #info(e: PointerEvent, kind: PointerKind): StrokeInfo {
    return { kind, shift: e.shiftKey, alt: e.altKey, meta: e.metaKey || e.ctrlKey, secondary: e.button === 2 || (e.buttons & 2) !== 0 };
  }

  #down = (e: PointerEvent): void => {
    if (!this.#enabled) return;
    if (e.cancelable) e.preventDefault();
    this.#rect = this.el.getBoundingClientRect();
    const kind = this.#kind(e);
    const p = this.#sample(e);
    const info = this.#info(e, kind);
    if (kind === 'pen') this.#penSeen = true;
    // Palm rejection: a pen touching down cancels any in-progress finger gesture.
    if (kind === 'pen') {
      for (const a of this.#active.values()) if (a.kind === 'touch') this.#abortActive(a);
      this.#active.clear();
      this.#pinchPrev = null;
    } else if (kind === 'touch' && [...this.#active.values()].some((a) => a.kind === 'pen')) {
      return; // ignore fingers while the pen is down
    }
    try {
      this.el.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    const singles = [...this.#active.values()].filter((a) => a.role === 'tool' || a.role === 'pan' || a.role === 'hold');
    // a finger left down from a pinch (or more fingers than two): a finger landing now is part of the
    // gesture, never a tap of the tool — a pinch let go of by one finger and taken up again goes on
    const pinching = [...this.#active.values()].filter((a) => a.kind === 'touch' && (a.role === 'pinch' || a.role === 'ignored'));
    let role: Active['role'] = 'tool';
    if (kind === 'touch' && pinching.length && !singles.length) {
      this.#pair = null;
      const rest = pinching.filter((a) => a.role === 'ignored');
      if (pinching.length === 1 && rest.length === 1) {
        const other = rest[0]!;
        other.role = 'pinch';
        role = 'pinch';
        this.#pinchPrev = [other.last, p];
      } else role = 'ignored';
      this.#active.set(e.pointerId, { id: e.pointerId, kind, start: p, last: p, moved: false, info, role, longTimer: 0, longFired: false, holdTimer: 0 });
      return;
    }
    // a thumb resting at the edge where the iPad is held is no first finger of a pinch: it is let be,
    // and the finger that lands now does what a finger does
    const resting = (a: Active) => a.kind === 'touch' && !a.moved && p.t - a.start.t > 350 && (a.start.x < EDGE || a.start.y < EDGE || a.start.x > this.#rect.width - EDGE || a.start.y > this.#rect.height - EDGE);
    if (singles.length === 1 && kind === 'touch' && resting(singles[0]!)) {
      const thumb = singles[0]!;
      clearTimeout(thumb.longTimer);
      clearTimeout(thumb.holdTimer);
      if (thumb.role === 'tool') this.#h.onCancel();
      else if (thumb.role === 'hold') this.#h.onHoldCancel?.();
      else if (thumb.role === 'pan') this.#h.onPanEnd?.(false);
      thumb.role = 'ignored';
      singles.length = 0;
    }
    if (singles.length === 1 && kind === 'touch' && singles[0]!.kind === 'touch') {
      // Second finger: convert to pinch.
      const first = singles[0]!;
      if (first.role === 'tool') this.#h.onCancel();
      if (first.role === 'hold') {
        clearTimeout(first.holdTimer);
        this.#h.onHoldCancel?.();
      }
      first.role = 'pinch';
      clearTimeout(first.longTimer);
      role = 'pinch';
      this.#pinchPrev = [first.last, p];
      this.#pair = p.t - first.start.t <= this.opts.twoFingerTapTime && !first.moved ? { t0: first.start.t, moved: false, lifted: 0 } : null;
    } else if (singles.length >= 1) {
      role = 'ignored';
    }
    const a: Active = { id: e.pointerId, kind, start: p, last: p, moved: false, info, role, longTimer: 0, longFired: false, holdTimer: 0 };
    this.#active.set(e.pointerId, a);
    if (role !== 'tool') return;
    const action = this.panMode ? 'pan' : this.#h.onDown(p, info);
    if (action === 'pan' || (action === 'draw' && kind === 'touch' && this.opts.fingerPansAfterPen && this.#penSeen)) {
      a.role = 'pan';
      return;
    }
    if (action === 'point') return;
    if (action === 'hold') {
      a.role = 'hold';
      this.#h.onHoldStart?.(p);
      a.holdTimer = window.setTimeout(() => {
        if (this.#active.get(a.id) !== a || a.role !== 'hold' || a.moved) return;
        a.role = 'tool';
        a.start = a.last;
        this.#h.onDrawStart?.(a.last, a.info);
      }, this.opts.holdTime);
      return;
    }
    // grab / draw: the tool stroke starts now. (A grab is never also a long press: a finger resting
    // on what it took, or carrying it slowly — a dot nudged under the loupe — keeps it in hand; held
    // still and lifted, it is selected, as by a tap.)
    if (action === 'draw') this.#h.onDrawStart?.(p, info);
  };

  #move = (e: PointerEvent): void => {
    if (!this.#enabled) return;
    const a = this.#active.get(e.pointerId);
    if (!a) {
      if (this.#h.onHover && e.pointerType !== 'touch' && this.#active.size === 0) this.#h.onHover(this.#sample(e));
      return;
    }
    if (e.cancelable) e.preventDefault();
    const p = this.#sample(e);
    if (!a.moved && Math.hypot(p.x - a.start.x, p.y - a.start.y) > this.opts.tapSlop) {
      a.moved = true;
      clearTimeout(a.longTimer);
      if (a.role === 'hold') {
        // Moved before the hold armed: this is a pan.
        clearTimeout(a.holdTimer);
        a.role = 'pan';
        this.#h.onHoldCancel?.();
      }
    }
    const prev = a.last;
    a.last = p;
    if (a.longFired || a.role === 'ignored' || a.role === 'hold') return;
    if (a.role === 'pinch') {
      if (this.#pair && Math.hypot(p.x - a.start.x, p.y - a.start.y) > this.opts.tapSlop) this.#pair.moved = true;
      const pair = [...this.#active.values()].filter((x) => x.role === 'pinch');
      if (pair.length === 2 && this.#pinchPrev) {
        const [x, y] = pair as [Active, Active];
        const a1 = x.last,
          b1 = y.last;
        const [a0, b0] = this.#pinchPrev;
        this.#h.onPinch(a0, b0, a1, b1);
        this.#pinchPrev = [a1, b1];
      }
      return;
    }
    if (a.role === 'pan') {
      this.#onPan?.(p.x - prev.x, p.y - prev.y);
      return;
    }
    // Tool stroke: deliver every coalesced sample for smooth Pencil strokes.
    let samples: Sample[];
    const coalesced = typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : [];
    if (coalesced.length > 1) samples = coalesced.map((c) => this.#sample(c));
    else samples = [p];
    this.#h.onMove(samples, p, a.info);
  };

  #finish(e: PointerEvent, cancelled: boolean): void {
    const a = this.#active.get(e.pointerId);
    if (!a) return;
    this.#active.delete(e.pointerId);
    clearTimeout(a.longTimer);
    clearTimeout(a.holdTimer);
    if (a.role === 'hold') {
      this.#h.onHoldCancel?.();
      a.role = 'pan';
    }
    try {
      this.el.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    const p = this.#sample(e);
    if (a.role === 'pinch') {
      this.#pinchPrev = null;
      // Remaining finger becomes ignored until lifted (prevents accidental draws after pinch).
      for (const o of this.#active.values()) if (o.role === 'pinch') o.role = 'ignored';
      if (![...this.#active.values()].some((o) => o.role === 'pinch')) this.#h.onPinchEnd?.();
      if (this.#pair) this.#pair.lifted = 1;
      return;
    }
    if (this.#pair && this.#pair.lifted === 1 && a.role === 'ignored') {
      // the second of the two fingers is up: both lifted soon, neither moved — a two-finger tap
      const pr = this.#pair;
      this.#pair = null;
      if (!pr.moved && Math.hypot(p.x - a.start.x, p.y - a.start.y) <= this.opts.tapSlop && p.t - pr.t0 <= this.opts.twoFingerTapTime) this.#h.onTwoFingerTap?.();
      return;
    }
    if (a.role === 'ignored' || a.longFired) return;
    if (cancelled) {
      if (a.role === 'tool') this.#h.onCancel();
      // a pan the system took away still leaves the plan where it was carried
      else if (a.role === 'pan') this.#h.onPanEnd?.(false);
      return;
    }
    const dt = p.t - a.start.t;
    const wasTap = !a.moved && dt <= this.opts.tapTime;
    if (a.role === 'tool') this.#h.onUp(p, a.info, wasTap, dt);
    // every pan ends here, moved or not: until it does, the plan is still in motion (and a selected
    // shape's handles wait for it to come to rest)
    else if (a.role === 'pan') this.#h.onPanEnd?.(wasTap);
    if (wasTap) this.#tap(p, a.info);
  }
  #tap(p: Sample, info: StrokeInfo): void {
    const lt = this.#lastTap;
    if (lt && p.t - lt.t <= this.opts.doubleTapTime && Math.hypot(p.x - lt.p.x, p.y - lt.p.y) < 24 && this.#h.onDoubleTap) {
      this.#lastTap = null;
      this.#h.onDoubleTap(p, info);
    } else {
      this.#lastTap = { t: p.t, p };
      this.#h.onTap?.(p, info);
    }
  }
  #up = (e: PointerEvent): void => {
    if (e.cancelable) e.preventDefault();
    this.#finish(e, false);
  };
  #cancel = (e: PointerEvent): void => this.#finish(e, true);
  #leave = (e: PointerEvent): void => {
    if (!this.#active.has(e.pointerId)) this.#h.onHover?.(null);
  };
  #lost = (e: PointerEvent): void => {
    if (this.#active.has(e.pointerId)) this.#finish(e, true);
  };
  #abortActive(a: Active): void {
    clearTimeout(a.longTimer);
    clearTimeout(a.holdTimer);
    if (a.role === 'hold') this.#h.onHoldCancel?.();
    if (a.role === 'tool' && !a.longFired) this.#h.onCancel();
    // a pan or pinch cut short (the Pencil landing) leaves the plan where it was carried
    if (a.role === 'pan') this.#h.onPanEnd?.(false);
    if (a.role === 'pinch') this.#h.onPinchEnd?.();
    try {
      this.el.releasePointerCapture(a.id);
    } catch {
      /* ignore */
    }
  }
  #cancelAll(): void {
    for (const a of this.#active.values()) this.#abortActive(a);
    this.#active.clear();
    this.#pinchPrev = null;
  }

  #wheel = (e: WheelEvent): void => {
    if (!this.#enabled) return;
    e.preventDefault();
    const r = this.#rect;
    const pivot = { x: e.clientX - r.left, y: e.clientY - r.top };
    let dx = e.deltaX,
      dy = e.deltaY;
    if (e.deltaMode === 1) {
      dx *= 16;
      dy *= 16;
    } else if (e.deltaMode === 2) {
      dx *= r.width;
      dy *= r.height;
    }
    if (e.ctrlKey || e.metaKey) {
      // Trackpad pinch (macOS) or ctrl+wheel: exponential zoom.
      const factor = Math.exp(-dy * 0.01);
      this.#h.onWheelZoom(factor, pivot);
    } else {
      this.#h.onWheelPan(-dx, -dy);
    }
  };

  #gestureScale = 1;
  #gestureActive = false;
  #touchesDown(): boolean {
    for (const a of this.#active.values()) if (a.kind === 'touch') return true;
    return false;
  }
  #gestureStart = (e: Event): void => {
    if (e.cancelable) e.preventDefault();
    if (!this.#enabled || this.#touchesDown()) return;
    this.#rect = this.el.getBoundingClientRect();
    this.#gestureScale = (e as SafariGestureEvent).scale ?? 1;
    this.#gestureActive = true;
    this.#h.onGestureStart?.();
  };
  #gestureChange = (e: Event): void => {
    if (e.cancelable) e.preventDefault();
    if (!this.#gestureActive) return;
    const g = e as SafariGestureEvent;
    const scale = g.scale ?? 1;
    if (!(scale > 0) || this.#gestureScale <= 0) return;
    const factor = scale / this.#gestureScale;
    this.#gestureScale = scale;
    const r = this.#rect;
    const pivot = { x: (g.clientX ?? r.left + r.width / 2) - r.left, y: (g.clientY ?? r.top + r.height / 2) - r.top };
    if (Math.abs(factor - 1) > 1e-4) this.#h.onGestureZoom?.(factor, pivot);
  };
  #gestureEnd = (e: Event): void => {
    if (e.cancelable) e.preventDefault();
    if (!this.#gestureActive) return;
    this.#gestureActive = false;
    this.#h.onGestureEnd?.();
  };

  /** Recompute the cached bounding rect (call on resize). */
  relayout(): void {
    this.#rect = this.el.getBoundingClientRect();
  }
}
